import '.ak{position:fixed;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.i{flex-direction:column;}.ssr-plugin'
import '.fp{width:fit-content;}.ssr-plugin'
import '.f4{z-index:3;}.ssr-plugin'
import '.dc{top:0px;}.ssr-plugin'
import '.db{left:0px;}.ssr-plugin'
import '.f{width:100%;}.ssr-plugin'
import '.t{height:100%;}.ssr-plugin'
import '.ig{backdrop-filter:blur(4px);}.ssr-plugin'
import React, {
  useRef,
  useEffect,
  useState,
  Children,
  useCallback,
} from 'react'
import { styled, Style } from 'inlines'
import { OverlayTrigger } from './OverlayTrigger.js'
import { OverlayContent } from './OverlayContent.js'
import { createPortal } from 'react-dom'
import { useWindowResize } from '../../hooks/useWindowResize.js'
import { colors } from '../../tokens/colors.js'
import { positionLogic } from './utils.js'
import { PositionObserver } from './positionObserver.js'
import { Stack } from '../Stack/index.js'

const overlayInstances = new Set<() => void>()

export type OverlayProps = {
  children: React.ReactNode
  position?:
    | 'top'
    | 'left'
    | 'bottom'
    | 'right'
    | 'center'
    | 'bottom-center'
    | 'top-center'
  scrollableParent?: boolean
  cantCloseOnClickOutside?: boolean
  triggerEl?: React.ReactNode | HTMLElement
  open?: boolean
  openOnHover?: boolean
  onOpen?: () => void
  onClose?: () => void
  style?: Style
}

export interface OverlayContentProps {
  children: React.ReactNode
  style?: Style
}

const Overlay = ({
  children,
  position = 'bottom',
  scrollableParent,
  cantCloseOnClickOutside,
  open: openProp = false,
  openOnHover = false,
  // for the hook
  triggerEl,
  onOpen,
  onClose,
}: OverlayProps) => {
  const triggerRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(openProp)
  const closeRef = useRef<() => void>()

  useEffect(() => {
    setOpen(openProp)
  }, [openProp])

  const { width, height } = useWindowResize()

  const triggerElement = Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === OverlayTrigger,
  )

  const contentElement = Children.toArray(children).find(
    (child): child is React.ReactElement<OverlayContentProps> =>
      React.isValidElement(child) && child.type === OverlayContent,
  )

  const contentStyle =
    React.isValidElement(contentElement) && contentElement.props?.style
      ? contentElement.props.style
      : {}

  const updatePosition = useCallback(() => {
    // check if overlayRef includes a data-nested attribute
    if (triggerRef.current && overlayRef.current) {
      const rect =
        triggerEl && triggerEl instanceof HTMLElement
          ? triggerEl.getBoundingClientRect()
          : triggerRef.current.getBoundingClientRect()

      const overlayCur = overlayRef.current

      positionLogic(rect, overlayCur, position)
    }
  }, [position, open])

  // Create a stable close function
  const closeOverlay = useCallback(
    (event?: React.MouseEvent | MouseEvent) => {
      const visibleOverlays = document.querySelectorAll('[data-overlay="true"]')
      const lastOverlay = visibleOverlays[visibleOverlays.length - 1]
      // Stop propagation to prevent triggerRef onClick from firing
      if (event) {
        event.stopPropagation()
        event.preventDefault()
      }

      if (overlayRef.current === lastOverlay) {
        setOpen(false)
        onClose?.()
      }

      // if(open){
      //   setOpen(false)
      // }

      // if (onClose) {
      //   onClose()
      // }
    },
    [onClose],
  )

  // Store the close function in a ref so it's always current
  closeRef.current = closeOverlay

  // Track open overlays
  useEffect(() => {
    if (open) {
      // console.log('Adding overlay to instances')
      const closeFn = () => closeRef.current?.()
      overlayInstances.add(closeFn)

      return () => {
        // console.log('Removing overlay from instances')
        overlayInstances.delete(closeFn)
      }
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    onOpen?.()

    const handleClickOutside = (event: MouseEvent) => {
      if (cantCloseOnClickOutside) return

      const visibleOverlays = document.querySelectorAll('[data-overlay="true"]')
      const lastOverlay = visibleOverlays[visibleOverlays.length - 1]
      if (
        open &&
        overlayRef.current &&
        !overlayRef.current.contains(event.target as Node) &&
        !triggerRef.current?.contains(event.target as Node) &&
        overlayRef.current === lastOverlay
      ) {
        setOpen(false)
        onClose?.()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      const visibleOverlays = document.querySelectorAll('[data-overlay="true"]')
      const lastOverlay = visibleOverlays[visibleOverlays.length - 1]

      if (
        event.key === 'Escape' &&
        overlayRef.current &&
        overlayRef.current === lastOverlay
      ) {
        event.stopPropagation()
        event.preventDefault()
        setOpen(false)
        onClose?.()
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open, cantCloseOnClickOutside, onClose])

  useEffect(() => {
    if (!open || !triggerRef.current) return

    const positionObs = new PositionObserver((data) => {
      // Handle position updates
      if (open) {
        if (data.outOfViewport && data.x !== 0 && data.y !== 0) {
          setOpen(false)
          onClose?.()
        }
        updatePosition()
      }
    })

    positionObs.observe(triggerRef.current)

    return () => {
      positionObs.disconnect()
    }
  }, [open, width, height, updatePosition, onClose])

  return (
    <Stack
      ref={triggerRef}
      onClick={(e) => {
        if (!open) {
          e.stopPropagation()
          setOpen(true)
        }
      }}
      onMouseEnter={() => {
        openOnHover && setOpen(true)
      }}
      justify="start"
      align="center"
    >
      {triggerElement}

      {open
        ? scrollableParent
          ? createPortal(
              <styled.div className="ak a i fp f4"
                ref={overlayRef}
                data-overlay="true"
                style={{
                  ...contentStyle,
                  // visibility: show ? 'visible' : 'hidden',
                  // opacity: show ? 1 : 0,
                  // transition: 'visibility 0.4s, opacity 0.35s cubic-bezier(0.45, 1.45, 0.8, 1)',
                }}
              >
                {contentElement}
              </styled.div>,
              document.body,
            )
          : createPortal(
              <div className="ak dc db f4 f t ig"
                style={{
                  backgroundColor: colors.black80,}}
              >
                <styled.div className="ak"
                  onClick={(e) => e.stopPropagation()}
                  ref={overlayRef}
                  data-overlay="true"
                  style={{
                    // overflow: 'auto',
                    left: position === 'center' && '50%',
                    top: position === 'center' && '50%',
                    transform: position === 'center' && 'translate(-50%, -50%)',
                    ...contentStyle,
                  }}
                >
                  {contentElement}
                </styled.div>
              </div>,
              document.body,
            )
        : null}
    </Stack>
  )
}

Overlay.Trigger = OverlayTrigger
Overlay.Content = OverlayContent

Overlay.Close = () => {
  // console.log('Overlay.Close called, total instances:', overlayInstances.size)

  const closeFunctions = Array.from(overlayInstances)

  if (closeFunctions.length > 0) {
    const lastClose = closeFunctions[closeFunctions.length - 1]
    // console.log('Closing last overlay')
    lastClose()
  } else {
    // console.log('No overlays to close')
  }
}

Overlay.CloseAll = () => {
  // console.log('CloseAll called, total instances:', overlayInstances.size)

  const closeFunctions = Array.from(overlayInstances)
  // console.log('Closing', closeFunctions.length, 'overlays')

  for (const closeFn of closeFunctions) {
    closeFn()
  }
}

export { Overlay }
